import React, { useEffect } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import type { PlayerDiedEvent, PlayerRespawnedEvent } from '../../../shared/types/NetworkMessages';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';
import type { RemotePlayerManager } from '../network/RemotePlayerManager';

/**
 * KillCamBridge — passive React component that pans the local camera
 * to the killer's sprite during the dead_ghost window, then restores
 * follow on the local sprite after respawn.
 *
 * Why a Bridge (not a Phaser-side system): the camera needs the
 * `playerDied` / `playerRespawned` wire events that NetworkManager
 * already republishes onto EventBus. Mounting at the React tree gives
 * us symmetrical lifecycle (ref-counted listener, automatic teardown)
 * with no Phaser-side wiring. Mirrors `RemotePlayerVfxBridge`.
 *
 * Behavior:
 *   - `WIRE_PLAYER_DIED` for local sid + valid `killerSid`:
 *       save the currently-followed sprite, swap follow target to the
 *       killer's remote sprite. CameraManager.followLogic lerps over
 *       a few frames so the transition reads as "pan, then track".
 *   - `WIRE_PLAYER_DIED` for local sid + null `killerSid`
 *       (mob kill / suicide / disconnect-during-cast):
 *       pause follow only — corpse-stays (L1.3) keeps the dead sprite
 *       at the death location, so the camera holds on it via the
 *       saved scroll position.
 *   - `WIRE_PLAYER_RESPAWNED` for local sid:
 *       restore follow on the saved local sprite. If the saved sprite
 *       is somehow gone, fall back to CameraManager.restoreFollowing
 *       which uses its own remembered ref.
 *
 * Anti-scope: does NOT zoom or apply post-process during the kill-cam
 * — the existing grayscale CSS filter (DeathOverlay) is the visual
 * language for "you are dead", and stacking another camera effect on
 * top would over-cook the moment. Future polish (slight zoom on the
 * killer, frame-letterbox bars) can land in L3.x without touching
 * this bridge's contract.
 *
 * Plan: docs/plans (L3.2)
 */

function getLocalSid(): string | null {
  const w = (typeof window === 'undefined' ? null : window) as any;
  return w?.nm?.getRoom?.()?.sessionId ?? null;
}

function getCameraManager() {
  try {
    const cam = ServiceRegistry.getInstance().get('CameraSystem', { optional: true });
    return cam?.getManager() ?? null;
  } catch {
    return null;
  }
}

function getRemotePlayerManager(): RemotePlayerManager | null {
  try {
    const entitySystem = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
    return entitySystem?.getRemotePlayerManager?.() ?? null;
  } catch {
    return null;
  }
}

const KillCamBridgeInner: React.FC = () => {
  useEffect(() => {
    const bus = EventBus.getInstance();
    let savedLocalSprite: any | null = null;

    const onPlayerDied = (msg: PlayerDiedEvent) => {
      const localSid = getLocalSid();
      if (!localSid || msg?.victimSid !== localSid) return;

      const camManager = getCameraManager();
      if (!camManager) return;

      // Save the sprite the camera is currently locked to so we can
      // restore on respawn. Falls through to `restoreFollowing()` if
      // the ref turns out to be null at restore time.
      savedLocalSprite = camManager.getPlayerSprite?.() ?? null;

      const killerSid = msg.killerSid;
      if (!killerSid) {
        // Mob kill / synthesized killer with no sid → pan to corpse
        // (which is the saved scroll position) by just pausing follow.
        camManager.stopFollowing();
        gameLogger.info('camera', '🎥 [KillCam] No killer sid — holding camera on corpse position');
        return;
      }

      const killerSprite = getRemotePlayerManager()?.getSprite?.(killerSid);
      if (!killerSprite) {
        // Killer sprite not in the remote registry yet — bail to "hold
        // on corpse" rather than guess. Reconciler may add the sprite
        // shortly; the pan would jump on appear so it's not worth the
        // race condition.
        camManager.stopFollowing();
        gameLogger.warn('camera', '🎥 [KillCam] Killer sprite not found — holding on corpse', { killerSid });
        return;
      }

      camManager.startFollowing(killerSprite);
      gameLogger.info('camera', '🎥 [KillCam] Following killer sprite', { killerSid });
    };

    const onPlayerRespawned = (msg: PlayerRespawnedEvent) => {
      const localSid = getLocalSid();
      if (!localSid || msg?.victimSid !== localSid) return;

      const camManager = getCameraManager();
      if (!camManager) return;

      if (savedLocalSprite) {
        camManager.startFollowing(savedLocalSprite);
        savedLocalSprite = null;
        gameLogger.info('camera', '🎥 [KillCam] Restored follow on local sprite (respawn)');
      } else {
        camManager.restoreFollowing?.();
        gameLogger.info('camera', '🎥 [KillCam] Restored follow via fallback (respawn)');
      }
    };

    bus.on(EVENTS.WIRE_PLAYER_DIED, onPlayerDied as (msg: unknown) => void);
    bus.on(EVENTS.WIRE_PLAYER_RESPAWNED, onPlayerRespawned as (msg: unknown) => void);

    return () => {
      bus.off(EVENTS.WIRE_PLAYER_DIED, onPlayerDied as (msg: unknown) => void);
      bus.off(EVENTS.WIRE_PLAYER_RESPAWNED, onPlayerRespawned as (msg: unknown) => void);
    };
  }, []);

  return null;
};

/** Public wrapper. Always mounts; the handlers themselves filter on
 *  local-sid so mob/bot deaths in spectator view never hijack the
 *  camera. */
export const KillCamBridge: React.FC = () => {
  return <KillCamBridgeInner />;
};
